'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import withReactContent from 'sweetalert2-react-content'
import Swal, { SweetAlertOptions } from 'sweetalert2'
import Loading from '@components/loading'
import { isObject, mergeObjects } from '@utils/objects'
import { handleAction } from '@utils/router'

interface ButtonLinkBase extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  action: string|(() => void);
  type?: 'primary' | 'secondary' | 'yellow' | 'outline-primary' | 'outline-secondary' | 'link-primary' | 'danger';
  className?: string;
  isProcessing?: boolean;
  disabled?: boolean;
  target?: string;
  showLoading?: boolean;
  children: React.ReactNode;
}

type ButtonLinkPropsWithoutConfirmation = ButtonLinkBase & {
  confirmOptions?: never;
  onDenied?: never;
  onDismissed?: never;
}

type ButtonLinkPropsWithConfirmation = ButtonLinkBase & {
  confirmOptions: true | SweetAlertOptions | (() => SweetAlertOptions);
  onDenied?: () => void;
  onDismissed?: () => void;
}

export type ButtonLinkProps = (ButtonLinkPropsWithConfirmation | ButtonLinkPropsWithoutConfirmation)

/**
 * ButtonLink component that navigates based on an action.
 */
const ButtonLink: React.FC<ButtonLinkProps> = ({
  action,
  type = 'primary',
  className = '',
  isProcessing = false,
  disabled = false,
  target = '',
  confirmOptions,
  children,
  onDenied,
  onDismissed,
  showLoading = true,
  ...props
}) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const loadingColors: Record<string, string> = {
    primary: "white",
    secondary: "white",
    yellow: "brand-yellow",
    danger: "red",
    "outline-primary": "primary",
    "outline-secondary": "secondary",
    "link-primary": "primary",
  };

  const disabledClasses = disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer";
  const isProcessingClasses = isProcessing || isLoading ? "button-is-loading cursor-wait d-inline-flex flex-row" : "";

  const buttonClasses = `btn btn-${type} button-link px-3 ${isProcessingClasses} ${disabledClasses} ${className}`;
  const loadingTheme = loadingColors[type] || loadingColors.primary;

  const handler = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (isProcessing || isLoading || disabled) {
      return;
    }

    const triggerAction = () => {
      if (typeof action === 'function') {
        return handleAction({ action, setIsLoading });
      }

      if (target) {
        return handleAction({ action, target, setIsLoading });
      }

      return handleAction({ router, action, setIsLoading });
    };

    // Without confirmation
    if (typeof confirmOptions === 'undefined' || ! confirmOptions) {
      return triggerAction();
    }

    props = props as ButtonLinkPropsWithConfirmation;

    const defaultOptions = {
      title: 'Você tem certeza?',
      text: 'Essa ação não poderá ser desfeita',
      showCancelButton: true,
      showDenyButton: typeof onDenied === 'function' || false,
      confirmButtonText: 'Sim',
      denyButtonText: 'Não',
      cancelButtonText: 'Cancelar',
    };

    const userOptions = (typeof confirmOptions === 'function') ? confirmOptions() : (isObject(confirmOptions) ? confirmOptions : {});

    const options: SweetAlertOptions = mergeObjects(defaultOptions, userOptions);

    const swalReact = withReactContent(Swal);
    swalReact.fire(options).then((result) => {
      if (result.isConfirmed) {
        return triggerAction();
      }

      if (options.showDenyButton && result.isDenied && typeof onDenied === 'function') {
        return onDenied();
      }

      if (options.showCancelButton && result.isDismissed && typeof onDismissed === 'function') {
        return onDismissed();
      }
    });
  };

  return (
    <a href="#" onClick={handler} className={buttonClasses} {...props}>
      {children}
      {(isProcessing || isLoading) && showLoading && (
        <span className="ms-3 loading-wrapper">
          <Loading variant={loadingTheme} mode="none" size="button" />
        </span>
      )}
    </a>
  );
};

export default ButtonLink;
